//
// Splide Config
// --------------------------------------------------
// Checks page for panels utilising splide and initialises their splide sliders with configurations set here

import Swiper from 'swiper/bundle';
import { Thumbs, EffectFade, FreeMode, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
const _debounce = require('lodash.debounce');

window.Swiper = Swiper;
window.Navigation = Navigation;
window.Pagination = Pagination;

// Store scroll position globally
let savedScrollPosition = 0;

function closeLightBox(lightbox) {
    lightbox.dataset.visible = 'false';
    if (typeof PageUtils !== 'undefined') {
        PageUtils.bodyLockToggleOff();
    } else {
        // Get the saved scroll position
        const scrollY = savedScrollPosition;

        // Remove scroll lock styles
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.documentElement.style.overflow = '';

        // Restore the scroll position instantly (no animation)
        window.scrollTo({
            top: scrollY,
            left: 0,
            behavior: 'instant'
        });
    }

    // Show scroll-to-top button
    const scrollCta = document.querySelector('button[data-scroll-cta]');
    if (scrollCta) {
        scrollCta.classList.remove('hidden');
    }

    setTimeout(() => { lightbox.dataset.hidden = 'true'; }, 300);
}

function openLightBox(lightbox) {
    lightbox.dataset.hidden = 'false';
    if (typeof PageUtils !== 'undefined') {
        PageUtils.bodyLockToggleOn();
    } else {
        // Save current scroll position
        savedScrollPosition = window.scrollY;

        // Apply scroll lock
        document.body.style.position = 'fixed';
        document.body.style.top = `-${savedScrollPosition}px`;
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
    }

    // Hide scroll-to-top button
    const scrollCta = document.querySelector('button[data-scroll-cta]');
    if (scrollCta) {
        scrollCta.classList.add('hidden');
    }

    const siteHeader = document.getElementById('siteHeader');
    if (siteHeader) {
        siteHeader.dataset.visible = 'false';
    }
    document.body.dataset.navVisible = 'false';
    setTimeout(() => { lightbox.dataset.visible = 'true'; }, 50);
}

function updateNavButtons(swiper, prevEl, nextEl) {
    const prevButton = document.querySelector(prevEl);
    const nextButton = document.querySelector(nextEl);

    if (prevButton && nextButton) {
        prevButton.disabled = swiper.isBeginning;
        nextButton.disabled = swiper.isEnd;

        // Optionally, add/remove opacity styles for better UX
        prevButton.classList.toggle('opacity-50', swiper.isBeginning);
        prevButton.classList.toggle('pointer-events-none', swiper.isBeginning);

        nextButton.classList.toggle('opacity-50', swiper.isEnd);
        nextButton.classList.toggle('pointer-events-none', swiper.isEnd);
    }
}

function documentKeydownHandler(event, lightbox, swiper) {
    if (!lightbox.classList.contains('hidden')) {
        if (event.key === 'Escape') closeLightBox(lightbox);
        else if (event.key === 'ArrowRight') swiper.slideNext();
        else if (event.key === 'ArrowLeft') swiper.slidePrev();
    }
}

// Function to initialize home hero sliders
function initHomeHeroSliders(homeHeroHeaderPanel) {
    for (const panel of homeHeroHeaderPanel) {
        try {
            const swiperElement = panel.querySelector('.swiper[data-swiper-type="home-hero-swiper"]');
            if (!swiperElement) continue;

            const swiperWrapper = swiperElement.querySelector('.swiper-wrapper');
            if (!swiperWrapper) continue;

            const swiperIndex = swiperElement.dataset.swiperIndex;
            if (!swiperIndex) continue;

            const rawAutoplayDelay = parseInt(swiperElement.dataset.autoplayDelay, 10);
            const autoplayDelay = Number.isFinite(rawAutoplayDelay) ? rawAutoplayDelay : 5000;
            const autoplayEnabled = swiperElement.dataset.autoPlay === "true" && autoplayDelay > 0;

            const tilesContainer = panel.querySelector('[data-hero-tiles]');
            const tiles = tilesContainer ? Array.from(tilesContainer.querySelectorAll('[data-tile-index]')) : [];

            // Reveal swiper (was hidden via inline opacity to prevent flash)
            swiperElement.style.opacity = 1;

            // Initialize main swiper
            const mainSwiper = new Swiper(swiperElement, {
                loop: true,
                speed: 1000,
                effect: 'fade',
                fadeEffect: { crossFade: true },
                allowTouchMove: false,
                initialSlide: 0,
                autoplay: autoplayEnabled ? {
                    delay: autoplayDelay,
                    disableOnInteraction: false,
                    pauseOnMouseEnter: false,
                } : false,
                modules: [EffectFade],
            });

            // Z-index handling for fade transitions
            function updateActiveSlideZIndex() {
                const slides = swiperElement.querySelectorAll('.swiper-slide');
                slides.forEach((slide) => {
                    const buttonContainer = slide.querySelector('.swiper-no-swiping');
                    const isActive = slide.classList.contains('swiper-slide-active');
                    slide.style.zIndex = isActive ? '10' : '1';
                    if (buttonContainer) {
                        buttonContainer.style.zIndex = isActive ? '20' : '1';
                    }
                });
            }

            // Active tile + progress bar handling.
            // Fade-out duration matches the Swiper crossfade speed (1000ms) so the
            // progress bar exits in lockstep with the slide image fade.
            const tileFadeDuration = 1000;
            const tileFadeTimeouts = new WeakMap();

            function clearPendingTileReset(tile) {
                const pending = tileFadeTimeouts.get(tile);
                if (pending) {
                    clearTimeout(pending);
                    tileFadeTimeouts.delete(tile);
                }
            }

            function resetTileProgress(tile) {
                const progress = tile.querySelector('[data-tile-progress]');
                if (!progress) return;
                clearPendingTileReset(tile);
                // Fade opacity out, then snap transform back for the next activation.
                progress.style.transition = `opacity ${tileFadeDuration}ms ease-in-out`;
                progress.style.opacity = '0';
                const t = setTimeout(() => {
                    progress.style.transition = 'none';
                    progress.style.transform = 'scaleX(0)';
                    progress.style.opacity = '1';
                    tileFadeTimeouts.delete(tile);
                }, tileFadeDuration);
                tileFadeTimeouts.set(tile, t);
            }

            function startTileProgress(tile) {
                const progress = tile.querySelector('[data-tile-progress]');
                if (!progress) return;
                clearPendingTileReset(tile);
                progress.style.transition = 'none';
                progress.style.transform = 'scaleX(0)';
                progress.style.opacity = '1';
                // Force reflow so the next transform animates
                void progress.offsetWidth;
                progress.style.transition = `transform ${autoplayDelay}ms linear`;
                progress.style.transform = 'scaleX(1)';
            }

            function updateActiveTile(realIndex) {
                tiles.forEach((tile) => {
                    const tileIndex = parseInt(tile.dataset.tileIndex, 10);
                    const isActive = tileIndex === realIndex;
                    tile.classList.toggle('is-active', isActive);
                    if (!isActive) {
                        resetTileProgress(tile);
                    }
                });
            }

            function findActiveTile() {
                return tiles.find(t => t.classList.contains('is-active'));
            }

            // Slide change events — always reset autoplay + progress on slide change
            // (handles arrow clicks, tile clicks, and autoplay tick uniformly).
            // Swiper resets its own timer on auto-ticks but NOT on manual slide
            // changes, so we explicitly stop+start to keep the underlying timer
            // in sync with the visual progress bar.
            mainSwiper.on('slideChange', () => {
                updateActiveSlideZIndex();
                updateActiveTile(mainSwiper.realIndex);
                if (autoplayEnabled && mainSwiper.autoplay) {
                    mainSwiper.autoplay.stop();
                    mainSwiper.autoplay.start();
                    const activeTile = findActiveTile();
                    if (activeTile) startTileProgress(activeTile);
                }
            });

            // Kick off the initial progress animation once swiper has settled
            if (autoplayEnabled) {
                setTimeout(() => {
                    if (mainSwiper.autoplay && mainSwiper.autoplay.running) {
                        const activeTile = findActiveTile();
                        if (activeTile) startTileProgress(activeTile);
                    }
                }, 100);
            }

            // Linkless tiles (rendered as <button>) → switch slide on click.
            // Linked tiles (<a href>) → browser navigates naturally.
            tiles.forEach((tile) => {
                if (tile.tagName === 'BUTTON') {
                    tile.addEventListener('click', () => {
                        const idx = parseInt(tile.dataset.tileIndex, 10);
                        if (Number.isFinite(idx)) {
                            mainSwiper.slideToLoop(idx);
                        }
                    });
                }
            });

            // Hero arrows
            const mainNextBtn = panel.querySelector(`[data-swiper-go-next="home-hero-swiper-${swiperIndex}"]`);
            const mainPrevBtn = panel.querySelector(`[data-swiper-go-prev="home-hero-swiper-${swiperIndex}"]`);
            mainNextBtn?.addEventListener('click', () => mainSwiper.slideNext());
            mainPrevBtn?.addEventListener('click', () => mainSwiper.slidePrev());

            // Lock the arrow buttons during the slide fade transition to prevent
            // spam clicks. Swiper sets `animating: true` for the transition duration;
            // we mirror that with an `is-transitioning` class for CSS feedback.
            const arrowButtons = [mainNextBtn, mainPrevBtn].filter(Boolean);
            mainSwiper.on('slideChangeTransitionStart', () => {
                arrowButtons.forEach(btn => btn.classList.add('is-transitioning'));
            });
            mainSwiper.on('slideChangeTransitionEnd', () => {
                arrowButtons.forEach(btn => btn.classList.remove('is-transitioning'));
            });

            // Initial state
            updateActiveSlideZIndex();
            updateActiveTile(mainSwiper.realIndex || 0);
        } catch (error) {
            console.error('Error initializing home hero swiper:', error);
        }
    }
}

// Function to initialize slider header sliders
function initSliderHeaderSliders(sliderHeaderPanel) {
    for (const panel of sliderHeaderPanel) {
        try {
            const swiperElement = panel.querySelector('.swiper[data-swiper-type="slider-header"]');

            if (!swiperElement) continue;

            // Ensure swiper wrapper exists before initializing
            const swiperWrapper = swiperElement.querySelector('.swiper-wrapper');
            if (!swiperWrapper) continue;

            // Performance optimization: Only initialize if panel is visible
            if (panel.offsetParent === null) continue;

            const rawAutoplayDelay = parseInt(swiperElement.dataset.autoplayDelay, 10);
            const autoplayDelay = Number.isFinite(rawAutoplayDelay) ? rawAutoplayDelay : 5000;
            const autoplay = swiperElement.dataset.autoPlay === "true" && autoplayDelay > 0;
            const swiperIndex = swiperElement.dataset.swiperIndex;
            if (!swiperIndex) continue;

            const nextEl = `button[data-swiper-go-next="slider-header-${swiperIndex}"]`;
            const prevEl = `button[data-swiper-go-prev="slider-header-${swiperIndex}"]`;

        const sliderHeaderSwiper = new Swiper(swiperElement, {
            loop: true,
            speed: 1000,
            effect: 'fade',
            fadeEffect: {
                crossFade: true
            },
            autoplay: autoplay ? {
                delay: autoplayDelay,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
            } : false,
            navigation: {
                nextEl: nextEl,
                prevEl: prevEl,
            },
            pagination: {
                el: `[data-swiper-pagination="slider-header-${swiperIndex}"]`,
                clickable: true,
                renderBullet: function (index, className) {
                    return `<span class="${className} relative block w-12 h-1 bg-white/50 cursor-pointer transition-all duration-300 ease-in-out hover:bg-white/75 [&.swiper-pagination-bullet-active]:w-16 overflow-hidden">
                        <span class="absolute top-0 left-0 h-full bg-white transition-all duration-100 ease-linear progress-bar" style="width: 0%"></span>
                    </span>`;
                },
            },
            modules: [Navigation, Pagination, EffectFade],
        });

        // Progress bar animation using Swiper's autoplay events for perfect sync
        let currentProgressBar = null;

        function resetProgressBars() {
            const allProgressBars = swiperElement.querySelectorAll('.progress-bar');
            allProgressBars.forEach(bar => {
                bar.style.transition = 'none';
                bar.style.width = '0%';
            });
        }

        function startProgressAnimation() {
            if (!autoplay) return;

            // Find the currently active pagination bullet
            const activeSlide = swiperElement.querySelector('.swiper-pagination-bullet-active');
            if (activeSlide) {
                currentProgressBar = activeSlide.querySelector('.progress-bar');
                if (currentProgressBar) {
                    // Reset to 0% width
                    currentProgressBar.style.transition = 'none';
                    currentProgressBar.style.width = '0%';

                    // Start the animation with exact timing
                    requestAnimationFrame(() => {
                        currentProgressBar.style.transition = `width ${autoplayDelay}ms linear`;
                        currentProgressBar.style.width = '100%';
                    });
                }
            }
        }

        function stopProgressAnimation() {
            if (currentProgressBar) {
                // Get current computed width and freeze it
                const computedStyle = window.getComputedStyle(currentProgressBar);
                const currentWidth = computedStyle.width;
                currentProgressBar.style.transition = 'none';
                currentProgressBar.style.width = currentWidth;
            }
        }

        // Use Swiper's autoplay events for perfect synchronization
        if (autoplay) {
            // Start progress when autoplay starts
            sliderHeaderSwiper.on('autoplayStart', () => {
                resetProgressBars();
                startProgressAnimation();
            });

            // Reset progress on slide change and restart if autoplay is running
            sliderHeaderSwiper.on('slideChange', () => {
                resetProgressBars();
                if (sliderHeaderSwiper.autoplay.running) {
                    startProgressAnimation();
                }
            });

            // Stop progress when autoplay stops
            sliderHeaderSwiper.on('autoplayStop', () => {
                stopProgressAnimation();
            });

            // Start initial progress animation
            setTimeout(() => {
                if (sliderHeaderSwiper.autoplay.running) {
                    startProgressAnimation();
                }
            }, 100);
        }

            // Pause autoplay and progress on hover
            if (autoplay) {
                swiperElement.addEventListener('mouseenter', () => {
                    sliderHeaderSwiper.autoplay.stop();
                });

                swiperElement.addEventListener('mouseleave', () => {
                    sliderHeaderSwiper.autoplay.start();
                });
            }
        } catch (error) {
            console.error('Error initializing slider header swiper:', error);
        }
    }
}

window.addEventListener('load', () => {
    const swiperElementsPresent = !!document.querySelector('.swiper');

    const galleryPanel = document.querySelectorAll('.gallery-panel');
    const cardPanel = document.querySelectorAll('.card-panel-slider');
    const testimonialSliderPanel = document.querySelectorAll('.testimonial-panel-slider');
    const testimonialFaderPanel = document.querySelectorAll('.testimonial-panel-fader');
    const thumbSwiperPanel = document.querySelectorAll('.equipment-details-panel');
    const homeHeroHeaderPanel = document.querySelectorAll('.home-hero-header-panel');
    const clientsPanel = document.querySelectorAll('.clients-panel');
    const sliderHeaderPanel = document.querySelectorAll('.slider-header-panel');
    const vehicleHeaderSliderPanel = document.querySelectorAll('.vehicle-header-slider-panel');
    const vehicleMediaSliderPanel = document.querySelectorAll('.vehicle-media-slider-panel');
    const projectImageTriggers = document.querySelectorAll('[data-project-image-trigger]');
    const projectImagesLightbox = document.querySelector('[data-lightbox="project-images"]');

    if (swiperElementsPresent) {

        // START CLIENTS PANEL SLIDER
        for (const panel of clientsPanel) {
            const swiperElement = panel.querySelector('.swiper[data-swiper-type="clients-slider"]');

            if (swiperElement) {
                const mobileSlidePerPage = 2;
                const tabletSlidePerPage = 3;
                const autoplay = swiperElement.dataset.autoPlay === "true";
                const swiperIndex = swiperElement.dataset.swiperIndex;
                const nextEl = `button[data-swiper-go-next="client-slider-${swiperIndex}"]`;
                const prevEl = `button[data-swiper-go-prev="client-slider-${swiperIndex}"]`;

                const clientSwiper = new Swiper(swiperElement, {
                    loop: true,
                    speed: 750,
                    direction: 'horizontal',
                    allowTouchMove: true,
                    autoplay: autoplay ?
                        {
                            delay: 10000,
                            disableOnInteraction: false
                        } : false,
                    slidesPerView: mobileSlidePerPage,
                    spaceBetween: 64,
                    centeredSlides: false,
                    navigation: {
                        nextEl: nextEl,
                        prevEl: prevEl,
                    },
                    breakpoints: {
                        640: {
                            slidesPerView: mobileSlidePerPage,
                            spaceBetween: 64,
                        },
                        768: {
                            slidesPerView: tabletSlidePerPage,
                            spaceBetween: 64,
                        },
                        1024: {
                            slidesPerView: tabletSlidePerPage,
                            spaceBetween: 64,
                        },
                        1280: {
                            slidesPerView: 4,
                            spaceBetween: 80,
                        },
                    },
                });
            }
        }
        // END CLIENTS PANEL SLIDER

        // START HOME HERO THUMB SWIPER PANEL SLIDER
        initHomeHeroSliders(homeHeroHeaderPanel);
        // END HOME HERO THUMB SWIPER PANEL SLIDER

        // START THUMB SWIPER PANEL SLIDER
        for (const panel of thumbSwiperPanel) {
            const swiperElement = panel.querySelector('.swiper[data-swiper-type="thumb-swiper"]');
            const swiperElementThumbs = panel.querySelector('.swiper[data-swiper-type="thumb-swiper-thumbs"]');
            const swiperIndex = swiperElement.dataset.swiperIndex;
            const nextEl = `button[data-swiper-go-next="thumb-swiper-${swiperIndex}"]`;
            const prevEl = `button[data-swiper-go-prev="thumb-swiper-${swiperIndex}"]`;

            if (!!swiperElement && !!swiperElementThumbs) {
                let thumbSwiper;
                let thumbSwiperThumbs;

                thumbSwiperThumbs = new Swiper(swiperElementThumbs, {
                    loop: false,
                    spaceBetween: 16,
                    slidesPerView: 4,
                    freeMode: true,
                    watchSlidesProgress: true,
                    autoplay: swiperElementThumbs.dataset.autoPlay === 'true',
                });
                thumbSwiper = new Swiper(swiperElement, {
                    effect: 'fade',
                    fadeEffect: { crossFade: true },
                    loop: false,
                    spaceBetween: 16,
                    navigation: {
                        nextEl: nextEl,
                        prevEl: prevEl,
                    },
                    thumbs: {
                        swiper: thumbSwiperThumbs,
                    },
                });
            }
        }
        // END THUMB SWIPER PANEL SLIDER

        // START CARD PANEL SLIDER
        for (const panel of cardPanel) {
            const swiperElement = panel.querySelector('.swiper[data-swiper-type="card-slider"]');

            if (swiperElement) {
                const mobileSlidePerPage = 1.1;
                const tabletSlidePerPage = 2.1;
                const paginationBackgroundColor = swiperElement.dataset.paginationBackgroundColor;
                const paginationBorderColor = swiperElement.dataset.paginationBorderColor;
                const autoplay = swiperElement.dataset.autoPlay === "true";
                const swiperIndex = swiperElement.dataset.swiperIndex;
                const nextEl = `button[data-swiper-go-next="card-slider-${swiperIndex}"]`;
                const prevEl = `button[data-swiper-go-prev="card-slider-${swiperIndex}"]`;

                const cardSwiper = new Swiper(swiperElement, {
                    loop: false,
                    speed: 750,
                    direction: 'horizontal',
                    allowTouchMove: true,
                    autoplay: autoplay ?
                        {
                            delay: 10000,
                            disableOnInteraction: false
                        } : false,
                    slidesPerView: mobileSlidePerPage,
                    slidesPerGroup: 1,
                    spaceBetween: 24,
                    centeredSlides: false,
                    pagination: {
                        el: `[data-swiper-pagination="card-slider-${swiperIndex}"]`,
                        clickable: true,
                        renderBullet: function (index, className) {
                            return `<span class="${className} rounded-full w-2 h-2 cursor-pointer opacity-50 hover:opacity-75 hover:scale-125 origin-center transition-all duration-150 ease-in-out border ${paginationBackgroundColor} ${paginationBorderColor} [&.swiper-pagination-bullet-active]:!opacity-100 [&.swiper-pagination-bullet-active]:!scale-100"></span>`;
                        },
                    },
                    navigation: {
                        nextEl: nextEl,
                        prevEl: prevEl,
                    },
                    breakpoints: {
                        640: {
                            slidesPerView: mobileSlidePerPage,
                            spaceBetween: 24,
                        },
                        768: {
                            slidesPerView: tabletSlidePerPage,
                            spaceBetween: 24,
                        },
                        1024: {
                            slidesPerView: tabletSlidePerPage,
                            spaceBetween: 32,
                        },
                        1280: {
                            slidesPerView: 3,
                            spaceBetween: 32,
                        },
                    },
                });

                cardSwiper.on('reachBeginning reachEnd slideChange', function () {
                    updateNavButtons(cardSwiper, prevEl, nextEl);
                });
                updateNavButtons(cardSwiper, prevEl, nextEl);
            }
        }
        // END CARD PANEL SLIDER

        // START GALLERY PANEL SLIDER
        for (const panel of galleryPanel) {
            const swiperElement = panel.querySelector('.swiper[data-swiper-type="gallery-slider"]');

            if (swiperElement) {
                const galleryThumbnails = swiperElement.querySelectorAll('.gallery-panel-image');
                const lightbox = panel.querySelector('[data-lightbox="gallery-panel"]');
                const swiperElementLightbox = panel.querySelector('.swiper[data-swiper-type="gallery-lightbox-slider"]');

                const mobileSlidePerPage = 1.1;
                const tabletSlidePerPage = 2.1;
                const paginationBackgroundColor = swiperElement.dataset.paginationBackgroundColor;
                const paginationBorderColor = swiperElement.dataset.paginationBorderColor;
                const autoplay = swiperElement.dataset.autoPlay === "true";
                const destroySwiperDesktop = swiperElement.dataset.destroySwiperDesktop === "true";
                const swiperIndex = swiperElement.dataset.swiperIndex;
                const nextEl = `button[data-swiper-go-next="gallery-slider-${swiperIndex}"]`;
                const prevEl = `button[data-swiper-go-prev="gallery-slider-${swiperIndex}"]`;

                let gallerySwiper;
                let galleryLightboxSwiper;

                function initSwipers() {
                    const shouldDestroy = window.innerWidth >= 1024 && destroySwiperDesktop;

                    if (shouldDestroy) {
                        // Destroy swiper on desktop when in grid mode
                        if (gallerySwiper) {
                            gallerySwiper.destroy(true, true);
                            gallerySwiper = null;
                        }
                    } else {
                        // Initialize or keep swiper on mobile/tablet or when in slider mode
                        if (!gallerySwiper) {
                            gallerySwiper = new Swiper(swiperElement, {
                                loop: false,
                                speed: 350,
                                direction: 'horizontal',
                                allowTouchMove: true,
                                resistanceRatio: 0,
                                autoplay: autoplay ? { delay: 10000, disableOnInteraction: false } : false,
                                slidesPerView: mobileSlidePerPage,
                                slidesPerGroup: 1,
                                spaceBetween: 24,
                                pagination: {
                                    el: `[data-swiper-pagination="gallery-slider-${swiperIndex}"]`,
                                    clickable: true,
                                    renderBullet: function (index, className) {
                                        return `<span class="${className} rounded-full w-2 h-2 cursor-pointer opacity-50 hover:opacity-75 hover:scale-125 origin-center transition-all duration-150 ease-in-out ${paginationBackgroundColor} border ${paginationBorderColor} [&.swiper-pagination-bullet-active]:!opacity-100 [&.swiper-pagination-bullet-active]:!scale-100"></span>`;
                                    },
                                },
                                navigation: { nextEl: nextEl, prevEl: prevEl },
                                breakpoints: {
                                    640: { slidesPerView: mobileSlidePerPage, spaceBetween: 24 },
                                    768: { slidesPerView: tabletSlidePerPage, spaceBetween: 24 },
                                    1024: { slidesPerView: 3, spaceBetween: 32 },
                                    1280: { slidesPerView: 3, spaceBetween: 32 },
                                },
                            });

                            // Update nav buttons after initialization
                            if (gallerySwiper) {
                                gallerySwiper.on('reachBeginning reachEnd slideChange', function () {
                                    updateNavButtons(gallerySwiper, prevEl, nextEl);
                                });
                                updateNavButtons(gallerySwiper, prevEl, nextEl);
                            }
                        }
                    }
                }

                if (!!lightbox && !!swiperElementLightbox) {
                    // Move lightbox to body level to avoid z-index stacking context issues
                    if (lightbox.parentElement !== document.body) {
                        document.body.appendChild(lightbox);
                    }

                    galleryLightboxSwiper = new Swiper(swiperElementLightbox, {
                        effect: 'fade',
                        fadeEffect: { crossFade: true },
                        speed: 750,
                        loop: true,
                        centeredSlides: true,
                        resistanceRatio: 0,
                        autoplay: autoplay ? { delay: 10000, disableOnInteraction: false } : false,
                        allowTouchMove: true,
                    });
                }

                initSwipers();

                const handleResize = _debounce(() => {
                    initSwipers();
                }, 300);

                window.addEventListener("resize", handleResize);

                if (lightbox) {
                    const lightboxBackdrop = lightbox.querySelector('[data-lightbox-backdrop]');
                    const lightboxClose = lightbox.querySelector('[data-lightbox-close]');

                    if (lightboxBackdrop) {
                        lightboxBackdrop.addEventListener('click', () => closeLightBox(lightbox));
                    }
                    if (lightboxClose) {
                        lightboxClose.addEventListener('click', () => closeLightBox(lightbox));
                    }

                    // ESC key handler
                    document.addEventListener('keydown', (e) => {
                        if (e.key === 'Escape' && lightbox.dataset.visible === 'true') {
                            closeLightBox(lightbox);
                        }
                    });
                }

                let syncing = false;

                if (galleryLightboxSwiper) {
                    galleryLightboxSwiper.on('slideChange', function () {
                        if (!syncing && gallerySwiper) {
                            syncing = true;
                            gallerySwiper.slideToLoop(galleryLightboxSwiper.realIndex, 0, false);
                            syncing = false;
                        }
                    });
                }

                galleryThumbnails.forEach(thumbnail => {
                    thumbnail.addEventListener('click', () => {
                        const slideIndex = parseInt(thumbnail.dataset.slide);
                        openLightBox(lightbox);

                        // Slide to the clicked image after lightbox is visible
                        if (galleryLightboxSwiper) {
                            setTimeout(() => {
                                galleryLightboxSwiper.update();
                                galleryLightboxSwiper.slideToLoop(slideIndex, 0);
                            }, 100);
                        }
                    });
                });
            }
        }
        // END GALLERY PANEL SLIDER

        // START TESTIMONIAL PANEL SLIDER
        for (const panel of testimonialSliderPanel) {
            const swiperElement = panel.querySelector('.swiper[data-swiper-type="testimonial-slider"]');

            if (swiperElement) {
                const mobileSlidePerPage = 1.1;
                const tabletSlidePerPage = 2.1;
                const paginationBackgroundColor = swiperElement.dataset.paginationBackgroundColor;
                const paginationBorderColor = swiperElement.dataset.paginationBorderColor;
                const autoplay = swiperElement.dataset.autoPlay === "true";
                const swiperIndex = swiperElement.dataset.swiperIndex;
                const nextEl = `button[data-swiper-go-next="testimonial-slider-${swiperIndex}"]`;
                const prevEl = `button[data-swiper-go-prev="testimonial-slider-${swiperIndex}"]`;

                const testimonialSwiper = new Swiper(swiperElement, {
                    loop: false,
                    speed: 750,
                    direction: 'horizontal',
                    allowTouchMove: true,
                    autoplay: autoplay ?
                        {
                            delay: 10000,
                            disableOnInteraction: false
                        } : false,
                    slidesPerView: mobileSlidePerPage,
                    slidesPerGroup: 1,
                    spaceBetween: 24,
                    centeredSlides: false,
                    pagination: {
                        el: `[data-swiper-pagination="testimonial-slider-${swiperIndex}"]`,
                        clickable: true,
                        renderBullet: function (index, className) {
                            return `<span class="${className} rounded-full w-2 h-2 cursor-pointer opacity-50 hover:opacity-75 hover:scale-125 origin-center transition-all duration-150 ease-in-out border ${paginationBackgroundColor} ${paginationBorderColor} [&.swiper-pagination-bullet-active]:!opacity-100 [&.swiper-pagination-bullet-active]:!scale-100"></span>`;
                        },
                    },
                    navigation: {
                        nextEl: nextEl,
                        prevEl: prevEl,
                    },
                    breakpoints: {
                        640: {
                            slidesPerView: mobileSlidePerPage,
                            spaceBetween: 24,
                        },
                        768: {
                            slidesPerView: tabletSlidePerPage,
                            spaceBetween: 24,
                        },
                        1024: {
                            slidesPerView: tabletSlidePerPage,
                            spaceBetween: 32,
                        },
                        1450: {
                            slidesPerView: 2,
                            spaceBetween: 32,
                        },
                    },
                });

                // Function to toggle navigation button visibility
                testimonialSwiper.on('reachBeginning reachEnd slideChange', function () {
                    updateNavButtons(testimonialSwiper, prevEl, nextEl);
                });

                updateNavButtons(testimonialSwiper, prevEl, nextEl);
            }
        }
        // END TESTIMONIAL PANEL SLIDER

        // START TESTIMONIAL PANEL FADER
        for (const panel of testimonialFaderPanel) {
            const swiperElement = panel.querySelector('.swiper[data-swiper-type="testimonial-fader"]');

            if (swiperElement) {
                const paginationBackgroundColor = swiperElement.dataset.paginationBackgroundColor;
                const paginationBorderColor = swiperElement.dataset.paginationBorderColor;
                const autoplay = swiperElement.dataset.autoPlay === "true";
                const swiperIndex = swiperElement.dataset.swiperIndex;
                const nextEl = `button[data-swiper-go-next="testimonial-fader-${swiperIndex}"]`;
                const prevEl = `button[data-swiper-go-prev="testimonial-fader-${swiperIndex}"]`;

                const testimonialSwiper = new Swiper(swiperElement, {
                    effect: 'fade',
                    fadeEffect: {
                        crossFade: true,
                    },
                    loop: true,
                    speed: 750,
                    direction: 'horizontal',
                    allowTouchMove: true,
                    autoplay: autoplay ?
                        {
                            delay: 10000,
                            disableOnInteraction: false
                        } : false,
                    slidesPerView: 1,
                    slidesPerGroup: 1,
                    pagination: {
                        el: `[data-swiper-pagination="testimonial-fader-${swiperIndex}"]`,
                        clickable: true,
                        renderBullet: function (index, className) {
                            return `<span class="${className} rounded-full w-2 h-2 cursor-pointer opacity-50 hover:opacity-75 hover:scale-125 origin-center transition-all duration-150 ease-in-out border ${paginationBackgroundColor} ${paginationBorderColor} [&.swiper-pagination-bullet-active]:!opacity-100 [&.swiper-pagination-bullet-active]:!scale-100"></span>`;
                        },
                    },
                    navigation: {
                        nextEl: nextEl,
                        prevEl: prevEl,
                    },
                    breakpoints: {
                        640: {
                            slidesPerView: 1,
                        },
                        768: {
                            slidesPerView: 1,
                        },
                        1024: {
                            slidesPerView: 1,
                        },
                        1450: {
                            slidesPerView: 1,
                        },
                    },
                });

                // Function to toggle navigation button visibility
                testimonialSwiper.on('reachBeginning reachEnd slideChange', function () {
                    updateNavButtons(testimonialSwiper, prevEl, nextEl);
                });

                updateNavButtons(testimonialSwiper, prevEl, nextEl);
            }
        }
        // END TESTIMONIAL PANEL FADER

        // START SLIDER HEADER PANEL
        initSliderHeaderSliders(sliderHeaderPanel);
        // END SLIDER HEADER PANEL

        // START VEHICLE HEADER SLIDER PANEL
        for (const panel of vehicleHeaderSliderPanel) {
            const swiperElement = panel.querySelector('.swiper[data-swiper-type="vehicle-header"]');

            if (swiperElement) {
                // Performance optimization: Only initialize if panel is visible
                if (panel.offsetParent === null) continue;

                const rawAutoplayDelay = parseInt(swiperElement.dataset.autoplayDelay, 10);
                const autoplayDelay = Number.isFinite(rawAutoplayDelay) ? rawAutoplayDelay : 5000;
                const autoplay = swiperElement.dataset.autoPlay === "true" && autoplayDelay > 0;
                const swiperIndex = swiperElement.dataset.swiperIndex;
                const nextEl = `button[data-swiper-go-next="vehicle-header-${swiperIndex}"]`;
                const prevEl = `button[data-swiper-go-prev="vehicle-header-${swiperIndex}"]`;

                const vehicleHeaderSwiper = new Swiper(swiperElement, {
                    loop: true,
                    speed: 1000,
                    effect: 'fade',
                    fadeEffect: {
                        crossFade: true
                    },
                    autoplay: autoplay ? {
                        delay: autoplayDelay,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true
                    } : false,
                    navigation: {
                        nextEl: nextEl,
                        prevEl: prevEl,
                    },
                    pagination: {
                        el: `[data-swiper-pagination="vehicle-header-${swiperIndex}"]`,
                        clickable: true,
                        renderBullet: function (index, className) {
                            return `<span class="${className} relative block w-12 h-1 bg-white/50 cursor-pointer transition-all duration-300 ease-in-out hover:bg-white/75 [&.swiper-pagination-bullet-active]:w-16 overflow-hidden">
                                <span class="absolute top-0 left-0 h-full bg-white transition-all duration-100 ease-linear progress-bar" style="width: 0%"></span>
                            </span>`;
                        },
                    },
                    modules: [Navigation, Pagination, EffectFade],
                });

                // Progress bar animation using Swiper's autoplay events for perfect sync
                let currentProgressBar = null;

                function resetProgressBars() {
                    const allProgressBars = swiperElement.querySelectorAll('.progress-bar');
                    allProgressBars.forEach(bar => {
                        bar.style.transition = 'none';
                        bar.style.width = '0%';
                    });
                }

                function startProgressAnimation() {
                    if (!autoplay) return;

                    // Find the currently active pagination bullet
                    const activeSlide = swiperElement.querySelector('.swiper-pagination-bullet-active');
                    if (activeSlide) {
                        currentProgressBar = activeSlide.querySelector('.progress-bar');
                        if (currentProgressBar) {
                            // Reset to 0% width
                            currentProgressBar.style.transition = 'none';
                            currentProgressBar.style.width = '0%';

                            // Start the animation with exact timing
                            requestAnimationFrame(() => {
                                currentProgressBar.style.transition = `width ${autoplayDelay}ms linear`;
                                currentProgressBar.style.width = '100%';
                            });
                        }
                    }
                }

                function stopProgressAnimation() {
                    if (currentProgressBar) {
                        // Get current computed width and freeze it
                        const computedStyle = window.getComputedStyle(currentProgressBar);
                        const currentWidth = computedStyle.width;
                        currentProgressBar.style.transition = 'none';
                        currentProgressBar.style.width = currentWidth;
                    }
                }

                // Use Swiper's autoplay events for perfect synchronization
                if (autoplay) {
                    // Start progress when autoplay starts
                    vehicleHeaderSwiper.on('autoplayStart', () => {
                        resetProgressBars();
                        startProgressAnimation();
                    });

                    // Reset progress on slide change and restart if autoplay is running
                    vehicleHeaderSwiper.on('slideChange', () => {
                        resetProgressBars();
                        if (vehicleHeaderSwiper.autoplay.running) {
                            startProgressAnimation();
                        }
                    });

                    // Stop progress when autoplay stops
                    vehicleHeaderSwiper.on('autoplayStop', () => {
                        stopProgressAnimation();
                    });

                    // Start initial progress animation
                    setTimeout(() => {
                        if (vehicleHeaderSwiper.autoplay.running) {
                            startProgressAnimation();
                        }
                    }, 100);
                }

                // Pause autoplay and progress on hover
                if (autoplay) {
                    swiperElement.addEventListener('mouseenter', () => {
                        vehicleHeaderSwiper.autoplay.stop();
                    });

                    swiperElement.addEventListener('mouseleave', () => {
                        vehicleHeaderSwiper.autoplay.start();
                    });
                }
            }
        }
        // END VEHICLE HEADER SLIDER PANEL

        // START VEHICLE MEDIA SLIDER PANEL
        for (const panel of vehicleMediaSliderPanel) {
            const swiperElement = panel.querySelector('.swiper[data-swiper-type="vehicle-media"]');

            if (swiperElement) {
                // Performance optimization: Only initialize if panel is visible
                if (panel.offsetParent === null) {
                    continue;
                }

                const rawAutoplayDelay = parseInt(swiperElement.dataset.autoplayDelay, 10);
                const autoplayDelay = Number.isFinite(rawAutoplayDelay) ? rawAutoplayDelay : 5000;
                const autoplay = swiperElement.dataset.autoPlay === "true" && autoplayDelay > 0;
                const swiperIndex = swiperElement.dataset.swiperIndex;
                const nextEl = `button[data-swiper-go-next="vehicle-media-${swiperIndex}"]`;
                const prevEl = `button[data-swiper-go-prev="vehicle-media-${swiperIndex}"]`;

                const vehicleMediaSwiper = new Swiper(swiperElement, {
                    loop: true,
                    speed: 1000,
                    effect: 'fade',
                    fadeEffect: {
                        crossFade: true
                    },
                    autoplay: autoplay ? {
                        delay: autoplayDelay,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true
                    } : false,
                    navigation: {
                        nextEl: nextEl,
                        prevEl: prevEl,
                    },
                    pagination: {
                        el: `[data-swiper-pagination="vehicle-media-${swiperIndex}"]`,
                        clickable: true,
                        renderBullet: function (index, className) {
                            return `<span class="${className} relative block w-12 h-1 bg-white/50 cursor-pointer transition-all duration-300 ease-in-out hover:bg-white/75 [&.swiper-pagination-bullet-active]:w-16 overflow-hidden">
                                <span class="absolute top-0 left-0 h-full bg-white transition-all duration-100 ease-linear progress-bar" style="width: 0%"></span>
                            </span>`;
                        },
                    },
                    modules: [Navigation, Pagination, EffectFade]
                });

                // Progress bar animation using Swiper's autoplay events for perfect sync
                let currentProgressBar = null;

                function resetProgressBars() {
                    const allProgressBars = swiperElement.querySelectorAll('.progress-bar');
                    allProgressBars.forEach(bar => {
                        bar.style.width = '0%';
                        bar.style.opacity = '0';
                    });
                    currentProgressBar = null;
                }

                function startProgressAnimation() {
                    const activeSlide = swiperElement.querySelector('.swiper-slide-active');
                    if (!activeSlide) return;

                    const activeBullet = swiperElement.querySelector('.swiper-pagination-bullet-active');
                    if (!activeBullet) return;

                    currentProgressBar = activeBullet.querySelector('.progress-bar');
                    if (currentProgressBar) {
                        currentProgressBar.style.opacity = '1';
                        currentProgressBar.style.transition = `width ${autoplayDelay}ms linear`;
                        currentProgressBar.style.width = '100%';
                    }
                }

                function stopProgressAnimation() {
                    if (currentProgressBar) {
                        currentProgressBar.style.transition = 'none';
                        currentProgressBar.style.width = '0%';
                        currentProgressBar.style.opacity = '0';
                    }
                }

                // Use Swiper's autoplay events for perfect synchronization
                if (autoplay) {
                    vehicleMediaSwiper.on('autoplayStart', () => {
                        resetProgressBars();
                        startProgressAnimation();
                    });

                    vehicleMediaSwiper.on('slideChange', () => {
                        resetProgressBars();
                        if (vehicleMediaSwiper.autoplay.running) {
                            startProgressAnimation();
                        }
                    });

                    vehicleMediaSwiper.on('autoplayStop', () => {
                        stopProgressAnimation();
                    });

                    // Start initial progress animation
                    setTimeout(() => {
                        if (vehicleMediaSwiper.autoplay.running) {
                            startProgressAnimation();
                        }
                    }, 100);
                }

                // Pause autoplay and progress on hover
                if (autoplay) {
                    swiperElement.addEventListener('mouseenter', () => {
                        vehicleMediaSwiper.autoplay.stop();
                    });

                    swiperElement.addEventListener('mouseleave', () => {
                        vehicleMediaSwiper.autoplay.start();
                    });
                }
            }
        }
        // END VEHICLE MEDIA SLIDER PANEL

        // START PROJECT IMAGES MODAL SLIDER
        let projectImagesSwiper = null;

        if (projectImagesLightbox) {
            const swiperElement = projectImagesLightbox.querySelector('.swiper[data-swiper-type="project-images-slider"]');
            const nextButton = projectImagesLightbox.querySelector('.project-modal-next');
            const prevButton = projectImagesLightbox.querySelector('.project-modal-prev');
            const paginationEl = projectImagesLightbox.querySelector('.project-modal-pagination');

            if (swiperElement) {
                projectImagesSwiper = new Swiper(swiperElement, {
                    loop: false,
                    speed: 1000,
                    effect: 'fade',
                    fadeEffect: {
                        crossFade: true
                    },
                    allowTouchMove: true,
                    grabCursor: true,
                    pagination: {
                        el: paginationEl,
                        clickable: true,
                        renderBullet: function (index, className) {
                            return `<span class="${className} relative block w-12 h-1 bg-white/50 cursor-pointer transition-all duration-300 ease-in-out hover:bg-white/75 [&.swiper-pagination-bullet-active]:bg-white [&.swiper-pagination-bullet-active]:w-16 overflow-hidden"></span>`;
                        },
                    },
                    modules: [Navigation, Pagination, EffectFade],

                });

                // Manual navigation button handlers to avoid conflicts
                if (nextButton && prevButton) {
                    nextButton.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        projectImagesSwiper.slideNext();
                    });

                    prevButton.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        projectImagesSwiper.slidePrev();
                    });
                }
            }
        }

        // Project image click handlers
        if (projectImageTriggers.length > 0 && projectImagesLightbox && projectImagesSwiper) {
            projectImageTriggers.forEach(trigger => {
                trigger.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    const cardIndex = parseInt(trigger.dataset.cardIndex);

                    // Open the lightbox
                    openLightBox(projectImagesLightbox);

                    // Update swiper and navigate to the clicked image
                    if (projectImagesSwiper && !isNaN(cardIndex)) {
                        // Update swiper to recalculate dimensions
                        setTimeout(() => {
                            projectImagesSwiper.update();
                            projectImagesSwiper.slideTo(cardIndex, 0); // 0 for immediate transition
                        }, 100);
                    }
                });
            });
        }

        // Close button handler for project images modal
        if (projectImagesLightbox) {
            const closeButton = projectImagesLightbox.querySelector('[data-lightbox-close]');
            const backdrop = projectImagesLightbox.querySelector('[data-lightbox-backdrop]');

            if (closeButton) {
                closeButton.addEventListener('click', () => {
                    closeLightBox(projectImagesLightbox);
                });
            }

            if (backdrop) {
                backdrop.addEventListener('click', () => {
                    closeLightBox(projectImagesLightbox);
                });
            }

            // ESC key handler
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && projectImagesLightbox.dataset.visible === 'true') {
                    closeLightBox(projectImagesLightbox);
                }
            });
        }
        // END PROJECT IMAGES MODAL SLIDER

    }
});
